"use client"

import { 
  ArrowLeft, 
  RefreshCw, 
  TrendingUp, 
  Users, 
  CheckCircle, 
  Clock, 
  Calendar,
  BarChart3,
  Activity,
  Download,
  MonitorSmartphone
} from 'lucide-react'
import { useStats } from '../hooks/useStats'
import { useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { convertToCSV, downloadCSV, getFormattedDateTime, prepareStatsForExport, type ExportUser } from '../utils/csvExport'

const STATS_API_BASE_URL = import.meta.env.VITE_API_URL as string

export default function StatsView() {
  const navigate = useNavigate()
  const { stats, loading, error, lastUpdate, refresh } = useStats(true, 30000) // Auto-refresh cada 30s
  const [exporting, setExporting] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<string>('VISITANTE')

  // Formatear fecha
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('es-MX', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    })
  }

  // Formatear hora
  const formatTime = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleTimeString('es-MX', { 
      hour: '2-digit', 
      minute: '2-digit' 
    })
  }

  // Formatear hora pico
  const formatHour = (hour: number | null) => {
    if (hour === null) return 'N/A'
    const period = hour >= 12 ? 'PM' : 'AM'
    const displayHour = hour % 12 || 12
    return `${displayHour}:00 ${period}`
  }

  // Exportar todos los datos
  const handleExportAll = async () => {
    try {
      setExporting(true)
      
      // 1. Obtener schema del dashboard
      const schemaResponse = await fetch(`${STATS_API_BASE_URL}/api/v1/dashboard`)
      if (!schemaResponse.ok) {
        throw new Error('Error al obtener el schema')
      }
      const dashboardData = await schemaResponse.json()
      const schema = dashboardData.data?.schema || {}
      
      // 2. Obtener datos de exportación
      const response = await fetch(`${STATS_API_BASE_URL}/api/v1/stats/export`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json()
      
      if (result.status !== 200) {
        throw new Error(result.message || 'Error al obtener datos de exportación')
      }

      const users = result.data.users as ExportUser[]
      const preparedData = prepareStatsForExport(users, schema)
      const csv = convertToCSV(preparedData)
      const filename = `estadisticas_completas_${getFormattedDateTime()}.csv`
      
      downloadCSV(csv, filename)
      
      console.log(`✅ Exportados ${users.length} registros con schema dinámico`)
    } catch (err) {
      console.error('Error al exportar:', err)
      alert('Error al exportar los datos. Por favor intente nuevamente.')
    } finally {
      setExporting(false)
    }
  }

  // Exportar solo impresos
  const handleExportPrinted = async () => {
    try {
      setExporting(true)
      
      // 1. Obtener schema del dashboard
      const schemaResponse = await fetch(`${STATS_API_BASE_URL}/api/v1/dashboard`)
      if (!schemaResponse.ok) {
        throw new Error('Error al obtener el schema')
      }
      const dashboardData = await schemaResponse.json()
      const schema = dashboardData.data?.schema || {}
      
      // 2. Obtener datos de exportación
      const response = await fetch(`${STATS_API_BASE_URL}/api/v1/stats/export`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json()
      
      if (result.status !== 200) {
        throw new Error(result.message || 'Error al obtener datos de exportación')
      }

      const users = result.data.users as ExportUser[]
      const printedUsers = users.filter(user => user.impreso === 'Sí')
      const preparedData = prepareStatsForExport(printedUsers, schema)
      const csv = convertToCSV(preparedData)
      const filename = `gafetes_impresos_${getFormattedDateTime()}.csv`
      
      downloadCSV(csv, filename)
      
      console.log(`✅ Exportados ${printedUsers.length} gafetes impresos`)
    } catch (err) {
      console.error('Error al exportar:', err)
      alert('Error al exportar los datos. Por favor intente nuevamente.')
    } finally {
      setExporting(false)
    }
  }

  // Exportar visitantes impresos
  const handleExportByCategory = async (category: string) => {
    try {
      setExporting(true)

      const schemaResponse = await fetch(`${STATS_API_BASE_URL}/api/v1/dashboard`)
      if (!schemaResponse.ok) throw new Error('Error al obtener el schema')
      const dashboardData = await schemaResponse.json()
      const schema = dashboardData.data?.schema || {}

      const response = await fetch(`${STATS_API_BASE_URL}/api/v1/stats/export`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      })
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)

      const result = await response.json()
      if (result.status !== 200) throw new Error(result.message || 'Error al obtener datos de exportación')

      const users = result.data.users as ExportUser[]
      const filtered = users.filter(
        user => user.impreso === 'Sí' && (user['Categoría'] === category || user['categoria'] === category)
      )
      const preparedData = prepareStatsForExport(filtered, schema)
      const csv = convertToCSV(preparedData)
      const slug = category.toLowerCase().replace(/[^a-z0-9]/g, '_')
      downloadCSV(csv, `impresos_${slug}_${getFormattedDateTime()}.csv`)

      console.log(`✅ Exportados ${filtered.length} impresos de categoría ${category}`)
    } catch (err) {
      console.error('Error al exportar:', err)
      alert('Error al exportar los datos. Por favor intente nuevamente.')
    } finally {
      setExporting(false)
    }
  }

  // Exportar solo pendientes
  const handleExportPending = async () => {
    try {
      setExporting(true)
      
      // 1. Obtener schema del dashboard
      const schemaResponse = await fetch(`${STATS_API_BASE_URL}/api/v1/dashboard`)
      if (!schemaResponse.ok) {
        throw new Error('Error al obtener el schema')
      }
      const dashboardData = await schemaResponse.json()
      const schema = dashboardData.data?.schema || {}
      
      // 2. Obtener datos de exportación
      const response = await fetch(`${STATS_API_BASE_URL}/api/v1/stats/export`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const result = await response.json()
      
      if (result.status !== 200) {
        throw new Error(result.message || 'Error al obtener datos de exportación')
      }

      const users = result.data.users as ExportUser[]
      const pendingUsers = users.filter(user => user.impreso === 'No')
      const preparedData = prepareStatsForExport(pendingUsers, schema)
      const csv = convertToCSV(preparedData)
      const filename = `gafetes_pendientes_${getFormattedDateTime()}.csv`
      
      downloadCSV(csv, filename)
      
      console.log(`✅ Exportados ${pendingUsers.length} gafetes pendientes`)
    } catch (err) {
      console.error('Error al exportar:', err)
      alert('Error al exportar los datos. Por favor intente nuevamente.')
    } finally {
      setExporting(false)
    }
  }

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Cargando estadísticas...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md">
          <div className="text-red-500 text-center mb-4">
            <Activity className="w-16 h-16 mx-auto mb-2" />
            <h2 className="text-xl font-bold">Error al cargar estadísticas</h2>
            <p className="text-gray-600 mt-2">{error}</p>
          </div>
          <button
            onClick={() => navigate('/')}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Volver al Panel
          </button>
        </div>
      </div>
    )
  }

  if (!stats) return null

  const { general, por_dia, por_dia_visitante, por_dia_kiosko, por_hora_hoy, ultimas_impresiones, campos_extra, actividad_reciente } = stats

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-6 h-6 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Estadísticas de Impresión</h1>
                <p className="text-sm text-gray-500">
                  Última actualización: {lastUpdate?.toLocaleTimeString('es-MX')}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={refresh}
                disabled={loading}
                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                <span>Actualizar</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Botones de exportación */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <Download className="w-6 h-6 text-green-600" />
              <h2 className="text-xl font-bold text-gray-900">Exportar Datos</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <button
              onClick={handleExportAll}
              disabled={exporting || !stats}
              className="flex items-center justify-center space-x-2 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="w-5 h-5" />
              <span>Exportar Todo ({stats?.general.total_asistentes || 0})</span>
            </button>
            <button
              onClick={handleExportPrinted}
              disabled={exporting || !stats}
              className="flex items-center justify-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CheckCircle className="w-5 h-5" />
              <span>Solo Impresos ({stats?.general.total_impresos || 0})</span>
            </button>
            <button
              onClick={handleExportPending}
              disabled={exporting || !stats}
              className="flex items-center justify-center space-x-2 bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Clock className="w-5 h-5" />
              <span>Solo Pendientes ({stats?.general.total_pendientes || 0})</span>
            </button>
            <div className="flex flex-col gap-2">
              <select
                value={selectedCategory}
                onChange={e => setSelectedCategory(e.target.value)}
                disabled={exporting || !stats}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {Object.keys(stats?.campos_extra?.categoria?.stats?.total ?? {}).map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <button
                onClick={() => handleExportByCategory(selectedCategory)}
                disabled={exporting || !stats || !selectedCategory}
                className="flex items-center justify-center space-x-2 bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Users className="w-5 h-5" />
                <span>
                  Impresos ({stats?.campos_extra?.categoria?.stats?.impresos?.[selectedCategory] || 0})
                </span>
              </button>
            </div>
          </div>
          {exporting && (
            <div className="mt-4 text-center text-sm text-gray-600">
              <div className="inline-flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                <span>Exportando datos...</span>
              </div>
            </div>
          )}
        </div>

        {/* Tarjetas de resumen general */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          <StatCard
            title="Total Asistentes"
            value={general.total_asistentes}
            icon={<Users className="w-8 h-8" />}
            color="bg-blue-500"
          />
          <StatCard
            title="Gafetes Impresos"
            value={general.total_impresos}
            icon={<CheckCircle className="w-8 h-8" />}
            color="bg-green-500"
            subtitle={`${general.porcentaje_impreso}%`}
          />
          <StatCard
            title="Gafetes en Kiosko"
            value={general.total_impresos_kiosko ?? 0}
            icon={<MonitorSmartphone className="w-8 h-8" />}
            color="bg-teal-500"
            subtitle="Auto registro"
          />
          <StatCard
            title="Pendientes"
            value={general.total_pendientes}
            icon={<Clock className="w-8 h-8" />}
            color="bg-orange-500"
            subtitle={`${(100 - general.porcentaje_impreso).toFixed(2)}%`}
          />
          <StatCard
            title="Últimas 24h"
            value={actividad_reciente.ultimas_24h}
            icon={<TrendingUp className="w-8 h-8" />}
            color="bg-purple-500"
            subtitle={`${actividad_reciente.horas_activas_24h} horas activas`}
          />
        </div>

        {/* Hora pico */}
        {actividad_reciente.hora_pico.hora !== null && (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Activity className="w-6 h-6 text-purple-600" />
              <h2 className="text-xl font-bold text-gray-900">Hora Pico (Últimos 7 días)</h2>
            </div>
            <div className="text-center py-4">
              <p className="text-4xl font-bold text-purple-600">
                {formatHour(actividad_reciente.hora_pico.hora)}
              </p>
              <p className="text-gray-600 mt-2">
                {actividad_reciente.hora_pico.cantidad} impresiones
              </p>
            </div>
          </div>
        )}

        {/* Tabla resumen 7-11 Jun */}
        {(() => {
          const RANGE_START = '2026-06-07'
          const RANGE_END   = '2026-06-11'
          const visitanteMap = Object.fromEntries((por_dia_visitante ?? []).map(d => [d.fecha, d.cantidad]))
          const kioskoMap    = Object.fromEntries((por_dia_kiosko    ?? []).map(d => [d.fecha, d.cantidad]))
          // Unión de fechas con datos en el rango
          const fechas = Array.from(new Set([
            ...(por_dia            ?? []).map(d => d.fecha),
            ...(por_dia_visitante  ?? []).map(d => d.fecha),
            ...(por_dia_kiosko     ?? []).map(d => d.fecha),
          ])).filter(f => f >= RANGE_START && f <= RANGE_END).sort().reverse()
          if (fechas.length === 0) return null
          const totalVisitante = fechas.reduce((s, f) => s + (visitanteMap[f] ?? 0), 0)
          const totalKiosko    = fechas.reduce((s, f) => s + (kioskoMap[f]    ?? 0), 0)
          return (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center space-x-3 mb-6">
                <Calendar className="w-6 h-6 text-purple-600" />
                <h2 className="text-xl font-bold text-gray-900">Gafetes por Día — 7 al 11 Jun</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-green-700 uppercase tracking-wider">Visitantes Impresos</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-purple-600 uppercase tracking-wider">Kiosko (todos)</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {fechas.map(fecha => (
                      <tr key={fecha} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{formatDate(fecha)}</td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-block bg-green-100 text-green-800 text-sm font-semibold px-3 py-1 rounded-full">
                            {visitanteMap[fecha] ?? 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-block bg-purple-100 text-purple-800 text-sm font-semibold px-3 py-1 rounded-full">
                            {kioskoMap[fecha] ?? 0}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50 font-semibold border-t-2 border-gray-300">
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-700">Total</td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-block bg-green-200 text-green-900 text-sm font-bold px-3 py-1 rounded-full">{totalVisitante}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-block bg-purple-200 text-purple-900 text-sm font-bold px-3 py-1 rounded-full">{totalKiosko}</span>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )
        })()}

        {/* Gráfico por día */}
        {por_dia.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center space-x-3 mb-6">
              <Calendar className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-bold text-gray-900">Impresiones por Día (Últimos 30 días)</h2>
            </div>
            <div className="space-y-3">
              {por_dia.map((day, index) => {
                const maxCantidad = Math.max(...por_dia.map(d => d.cantidad))
                const percentage = (day.cantidad / maxCantidad) * 100
                
                return (
                  <div key={index} className="flex items-center space-x-4">
                    <div className="w-32 text-sm text-gray-600 font-medium">
                      {formatDate(day.fecha)}
                    </div>
                    <div className="flex-1">
                      <div className="relative h-8 bg-gray-100 rounded-lg overflow-hidden">
                        <div
                          className="absolute left-0 top-0 h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-500"
                          style={{ width: `${percentage}%` }}
                        />
                        <div className="absolute inset-0 flex items-center justify-end px-3">
                          <span className="text-sm font-semibold text-gray-700">
                            {day.cantidad} impresiones
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Gráfico por hora (hoy) */}
        {por_hora_hoy.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center space-x-3 mb-6">
              <BarChart3 className="w-6 h-6 text-green-600" />
              <h2 className="text-xl font-bold text-gray-900">Impresiones por Hora (Hoy)</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {por_hora_hoy.map((hour, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 text-center border border-green-200"
                >
                  <p className="text-2xl font-bold text-green-700">{hour.cantidad}</p>
                  <p className="text-sm text-gray-600 mt-1">{formatHour(hour.hora)}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Estadísticas por campos extra */}
        {Object.keys(campos_extra).length > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center space-x-3 mb-6">
              <BarChart3 className="w-6 h-6 text-indigo-600" />
              <h2 className="text-xl font-bold text-gray-900">Desglose por Categorías</h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {Object.entries(campos_extra).map(([fieldName, fieldData]) => (
                <div key={fieldName} className="border border-gray-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">{fieldData.label}</h3>
                  <div className="space-y-2">
                    {Object.entries(fieldData.stats.total).map(([value, total]) => {
                      const impresos = fieldData.stats.impresos[value] || 0
                      const pendientes = fieldData.stats.pendientes[value] || 0
                      const percentage = (impresos / total) * 100
                      
                      return (
                        <div key={value} className="bg-gray-50 rounded p-3">
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-medium text-gray-700">{value}</span>
                            <span className="text-sm text-gray-500">Total: {total}</span>
                          </div>
                          <div className="flex space-x-2 text-sm">
                            <div className="flex-1 bg-green-100 rounded px-2 py-1 text-green-800 text-center">
                              Impresos: {impresos}
                            </div>
                            <div className="flex-1 bg-orange-100 rounded px-2 py-1 text-orange-800 text-center">
                              Pendientes: {pendientes}
                            </div>
                          </div>
                          <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-green-500 to-green-600 transition-all duration-500"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Últimas impresiones */}
        {ultimas_impresiones.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center space-x-3 mb-6">
              <Clock className="w-6 h-6 text-gray-600" />
              <h2 className="text-xl font-bold text-gray-900">Últimas 10 Impresiones</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nombre
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fecha y Hora
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {ultimas_impresiones.map((print) => (
                    <tr key={print.id_asistente} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {print.nombre} {print.apellido}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{print.email}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(print.fecha_impreso)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatTime(print.fecha_impreso)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Componente de tarjeta de estadística
interface StatCardProps {
  title: string
  value: number
  icon: React.ReactNode
  color: string
  subtitle?: string
}

function StatCard({ title, value, icon, color, subtitle }: StatCardProps) {
  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm text-gray-500 mb-1">{title}</p>
          <p className="text-3xl font-bold text-gray-900">{value.toLocaleString()}</p>
          {subtitle && (
            <p className="text-sm text-gray-600 mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`${color} text-white p-3 rounded-lg`}>
          {icon}
        </div>
      </div>
    </div>
  )
}
